import { z } from 'zod';

import { COMPOUND_OPERATOR_OR, creators, OPERATOR_IN, print } from '@atlaskit/jql-ast';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererKeyToPayload,
	type ActionStatusProps,
	type ActionValidationResult,
	type ActionWith,
	type SerializableValue,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import type { UpdateWorkItemFieldsAction } from '../../common/types';

import type { mainUpdateWorkItemActionQuery$data } from './__generated__/mainUpdateWorkItemActionQuery.graphql';

export const ISSUE_ID_FIELD_KEY = 'id';
export const ISSUE_KEY_FIELD_KEY = 'key';

const serializableValueSchema: z.ZodType<SerializableValue> = z.lazy(() =>
	z.union([
		z.string(),
		z.number(),
		z.boolean(),
		z.null(),
		z.undefined(),
		z.array(serializableValueSchema),
		z.record(serializableValueSchema),
	]),
);

const payloadSchema = z.object({
	issueId: z.string(),
	fieldKey: z.string().nonempty(),
	fieldValue: serializableValueSchema,
	sourceUrl: z.string().optional(),
	reasoning: z.string().optional(),
});

const actionSchema = z.object({
	key: z.literal(ActionKeys.UpdateWorkItemFields),
	data: payloadSchema,
});

/**
 * Determines whether a given string is a potentially valid Jira issue key as it must contain a hyphen between letters and numbers.
 *
 * @param issueKey - The string to evaluate.
 * @returns `true` if the string is a potentially valid Jira issue key; otherwise, `false`.
 */
export const isIssueKey = (issueKey: string): boolean =>
	issueKey.match(/^[A-Za-z0-9]+-\d+$/) !== null;

/**
 * Determines whether a given string is a potentially valid Jira issue ID as it must be an integer.
 *
 * @param issueId - The string to evaluate.
 * @returns `true` if the string is a potentially valid Jira issue ID; otherwise, `false`.
 */
export const isIssueId = (issueId: string): boolean => Number.isInteger(Number(issueId));

/**
 * Validates a Jira "Update Work Item Fields" action payload against the expected schema.
 *
 * @param action - The action object to validate.
 * @returns An object indicating whether the action is valid. If valid, returns the typed action.
 *          If invalid, returns an error describing the validation failure.
 */
export function validateJiraUpdateWorkItemFieldsAction(
	action: MessageAction,
): ActionValidationResult<ActionRendererKeyToPayload[typeof ActionKeys.UpdateWorkItemFields]> {
	const parsed = actionSchema.safeParse(action);
	if (parsed.success) {
		return {
			isValid: true,
			action: action as ActionWith<
				ActionRendererKeyToPayload[typeof ActionKeys.UpdateWorkItemFields]
			>,
		};
	}

	return {
		isValid: false,
		error: new Error(`Invalid ${action.key || 'unkown'} action payload: ${parsed.error.message}`),
	};
}

/**
 * Groups a list of actions by their associated Jira issue ARI.
 *
 * Each action is mapped to a Jira issue ARI, constructed using the provided `cloudId` and the action's `issueId`.
 * The function returns a Map where each key is an issue ARI and the value is an array of actions related to that issue.
 *
 * @param validActions - An array of actions to be grouped, each containing data for updating work item fields.
 * @param cloudId - The cloud/site ID used to construct the Jira issue ARI.
 * @returns A Map where keys are Jira issue ARIs and values are arrays of actions associated with each issue.
 */
export const groupActionsByIssue = (
	validActions: UpdateWorkItemFieldsAction[],
	cloudId: string,
	edges: NonNullable<
		NonNullable<mainUpdateWorkItemActionQuery$data['jira']>['issueSearch']
	>['edges'] = [],
): Map<string, UpdateWorkItemFieldsAction[]> =>
	validActions.reduce((groupedActions, action) => {
		const issueAri = actionToIssueAri(action, cloudId, edges);
		if (!issueAri) {
			return groupedActions;
		}

		const existingActions = groupedActions.get(issueAri) || [];
		groupedActions.set(issueAri, [...existingActions, action]);
		return groupedActions;
	}, new Map<string, UpdateWorkItemFieldsAction[]>());

/**
 * Builds a JQL (Jira Query Language) string for a set of actions that update work item fields.
 *
 * This function extracts unique issue IDs from the provided actions and constructs a JQL query
 * that matches all issues with those IDs using the "IN" operator. The resulting JQL string
 * can be used in relay queries or other Jira API requests.
 *
 * @param actions - An array of actions containing payloads for updating work item fields.
 * @returns A JQL query string that selects issues by their IDs.
 */
export const buildJqlForActions = (actions: UpdateWorkItemFieldsAction[]): string => {
	// Retrieve the issueIds or issueKeys to formulate the JQL in our relay query
	const issueIdsOrKeys = Array.from(new Set(actions.map((action) => action.data.issueId)));
	const issueIds = issueIdsOrKeys.filter((issueId) => isIssueId(issueId));
	const issueKeys = issueIdsOrKeys.filter((issueKey) => isIssueKey(issueKey));

	const jqlQuery = creators.query();

	if (issueIds.length > 0) {
		jqlQuery.appendClause(
			creators.terminalClause(
				creators.field(ISSUE_ID_FIELD_KEY),
				creators.operator(OPERATOR_IN),
				creators.listOperand(issueIds.map(creators.valueOperand)),
			),
			COMPOUND_OPERATOR_OR,
		);
	}

	if (issueKeys.length > 0) {
		jqlQuery.appendClause(
			creators.terminalClause(
				creators.field(ISSUE_KEY_FIELD_KEY),
				creators.operator(OPERATOR_IN),
				creators.listOperand(issueKeys.map(creators.valueOperand)),
			),
			COMPOUND_OPERATOR_OR,
		);
	}

	const jast = creators.jast(jqlQuery);

	return print(jast);
};

/**
 * Determines whether bulk action buttons should be disabled based on the provided actions.
 *
 * @param actions - An array of actions to evaluate.
 * @param getStatus - A function that returns the status of a given action.
 * @param getReadonly - A function that returns whether a given action is readonly.
 * @returns `true` if the bulk action buttons should be disabled (i.e., if there are no actions,
 *          or if any action is not idle or is readonly); otherwise, `false`.
 */
export function areBulkActionButtonsDisabled(
	actions: ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.UpdateWorkItemFields]>[],
	getStatus: ActionStatusProps['getStatus'],
	getReadonly: ActionStatusProps['getReadonly'],
): boolean {
	if (actions.length === 0) {
		return true;
	}
	return actions.some((action) => {
		if (!(getStatus(action) === 'idle' || getStatus(action) === 'finished')) {
			return true;
		}
		if (getReadonly(action) && getStatus(action) !== 'finished') {
			return true;
		}
		return false;
	});
}

/**
 * Converts an action to a Jira issue ARI.
 *
 * @param action - The action to convert to an ARI.
 * @param cloudId - The cloud/site ID used to construct the Jira issue ARI.
 * @param edges - The edges to search for the issue.
 * @returns A Jira issue ARI string or null if the action is not a valid issue ID or key.
 */
export const actionToIssueAri = (
	action: UpdateWorkItemFieldsAction,
	cloudId: string,
	edges: NonNullable<
		NonNullable<mainUpdateWorkItemActionQuery$data['jira']>['issueSearch']
	>['edges'] = [],
): string | null => {
	const issueIdOrKey = action.data.issueId;
	if (isIssueId(issueIdOrKey)) {
		return JiraIssueAri.create({
			siteId: cloudId,
			issueId: issueIdOrKey,
		}).toString();
		// As issueId may sometimes be hallucinated by the LLM to be an issueKey, we need to check if
		// we can retrieve the ARI from the issue itself.
	}
	if (isIssueKey(issueIdOrKey)) {
		const issue = edges?.find((edge) => edge?.node?.key === action.data.issueId);
		// If issue can be found, use its ARI, otherwise return null.
		if (issue?.node?.id) {
			return issue?.node?.id;
		}
	}
	return null;
};

/**
 * Deduplicates a list of actions by their field key and issue ID.
 * @param actions - An array of actions to deduplicate.
 * @returns An array of deduplicated actions.
 *
 * @example
 * ```ts
 * const actions = [
 *   { key: 'updateWorkItemFields', data: { fieldKey: 'summary', issueId: '123' } },
 *   { key: 'updateWorkItemFields', data: { fieldKey: 'summary', issueId: '123' } },
 * ];
 * const deduplicatedActions = deduplicateActions(actions);
 * console.log(deduplicatedActions); // [{ key: 'updateWorkItemFields', data: { fieldKey: 'summary', issueId: '123' } }]
 */
export const deduplicateActions = (
	actions: UpdateWorkItemFieldsAction[],
): UpdateWorkItemFieldsAction[] => {
	const deduplicatedActions: UpdateWorkItemFieldsAction[] = [];
	const seenActions = new Set<string>();
	for (const action of actions) {
		const { fieldKey, issueId } = action.data;
		const uniqueKey = `${fieldKey}:${issueId}`;
		if (!seenActions.has(uniqueKey)) {
			seenActions.add(uniqueKey);
			deduplicatedActions.push(action);
		}
	}
	return deduplicatedActions;
};

/**
 * Capitalises the issueId field in each action.
 *
 * When issueId is an issueKey and it is not capitalised, it cannot be matched to an issue edge
 * as issue keys are returned as capitalised in the Relay query. Refer to
 * https://atlassian.slack.com/archives/C09RX5L3BEC/p1765758243635049 for more context.
 *
 * @param actions - An array of actions to capitalise the issueId field of.
 * @returns An array of actions with the issueId field capitalised.
 *
 * @example
 * ```ts
 * const actions = [
 *   { key: 'updateWorkItemFields', data: { issueId: 'abc-123' } },
 *   { key: 'updateWorkItemFields', data: { issueId: 'abc-123' } },
 * ];
 * const capitalisedActions = capitaliseActionIssueIds(actions);
 * console.log(capitalisedActions); // [{ key: 'updateWorkItemFields', data: { issueId: 'ABC-123' } }, { key: 'updateWorkItemFields', data: { issueId: 'ABC-123' } }]
 */
export const capitaliseActionIssueIds = (
	actions: UpdateWorkItemFieldsAction[],
): UpdateWorkItemFieldsAction[] =>
	actions.map((action) => ({
		...action,
		data: {
			...action.data,
			issueId: action.data.issueId.toUpperCase(),
		},
	}));
